'use client';

import { type RefObject, useEffect, useRef } from 'react';
import useGridStore from '@/publicSearch/store/useGridStore';

/**
 * Frames (about half a second) a restore keeps trying to reach the saved
 * position before it settles for as far as the list scrolls.
 */
export const MAX_RESTORE_FRAMES = 30;

/**
 * The nearest ancestor that scrolls vertically: the page's scroller for a
 * list that takes its natural height (the public-search layout box below md).
 * Stops before `<body>`/`<html>`: document scrolling fires its `scroll`
 * events on `window`, not on those elements.
 */
export function findScrollParent(element: HTMLElement): HTMLElement | null {
  for (
    let node = element.parentElement;
    node && node !== document.body && node !== document.documentElement;
    node = node.parentElement
  ) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
}

/**
 * Scrolls to `target` once the list is tall enough to get there, re-applying
 * it until the scroller's height stops changing: auto-height rows (the compact
 * phone lists) are measured a frame or two after they render. Gives up after
 * `maxFrames`, leaving the list as far down as it goes. `getScroller` is read
 * every frame so a scroller swap mid-restore (the grid switching to autoHeight
 * after mount) lands on the new one. Returns a function that cancels it.
 */
function restoreScrollTop(
  getScroller: () => HTMLElement | null,
  target: number,
  onDone: () => void,
  maxFrames: number = MAX_RESTORE_FRAMES,
): () => void {
  let frames = 0;
  let lastScrollHeight = -1;
  let handle = 0;

  const step = () => {
    frames += 1;
    const scroller = getScroller();
    const outOfFrames = frames >= maxFrames;
    if (scroller) {
      // scrollTop can be fractional, scrollHeight/clientHeight are not.
      const reachable =
        scroller.scrollHeight - scroller.clientHeight >= target - 1;
      if (reachable || outOfFrames) scroller.scrollTop = target;
      const settled = reachable && scroller.scrollHeight === lastScrollHeight;
      lastScrollHeight = scroller.scrollHeight;
      if (settled || outOfFrames) {
        onDone();
        return;
      }
    } else if (outOfFrames) {
      onDone();
      return;
    }
    handle = window.requestAnimationFrame(step);
  };

  handle = window.requestAnimationFrame(step);
  return () => window.cancelAnimationFrame(handle);
}

type GridScrollRestoreOptions = {
  /**
   * Identifies the list on screen. The saved position belongs to one key:
   * mounting again with it ("Back to lot listing") restores the position, and
   * a new key (another page, filter or sale) starts at the top. Without a key
   * the list keeps no position.
   */
  scrollKey: string | undefined;
  /** The element that scrolls the list: its own, or the page's. */
  getScroller: () => HTMLElement | null;
  /** The list's element (the grid, or the cards), where it starts on the page. */
  listRef: RefObject<HTMLElement | null>;
  /** True once the rows of the current page are in the grid. */
  rowsReady: boolean;
};

/**
 * Brings the first row into view after the list changes. The page's scroller
 * holds the header above the list, so it only comes up to the list's top, and
 * not at all when the user is above it (e.g. typing in the search box).
 */
function scrollToListTop(scroller: HTMLElement, list: HTMLElement | null) {
  const listTop =
    list && scroller.contains(list)
      ? scroller.scrollTop +
        list.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top
      : 0;
  if (scroller.scrollTop > listTop) scroller.scrollTop = Math.max(0, listTop);
}

/**
 * Keeps a results list's scroll position (the grid or the cards) in the grid
 * store (persisted per tab) so returning to the list lands where the user
 * left it.
 */
export function useGridScrollRestore({
  scrollKey,
  getScroller,
  listRef,
  rowsReady,
}: GridScrollRestoreOptions) {
  // The saved position still to put back once the rows are in; null when
  // there is nothing to restore.
  const pendingRestoreRef = useRef<number | null>(null);
  // The key this grid last applied, to tell a change of list while mounted
  // from a (re)mount.
  const appliedKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (scrollKey === undefined) return;
    const previousKey = appliedKeyRef.current;
    if (previousKey === scrollKey) return;
    appliedKeyRef.current = scrollKey;

    const saved = useGridStore.getState();
    if (previousKey === undefined && saved.scrollKey === scrollKey) {
      // Mounted again on the list the position was kept for: restore it
      // once the rows are in. The rows load after mount, so scrolling now
      // would clamp to 0.
      pendingRestoreRef.current =
        saved.scrollPosition > 0 ? saved.scrollPosition : null;
      return;
    }

    pendingRestoreRef.current = null;
    const scroller = getScroller();
    if (previousKey !== undefined && scroller) {
      // The list changed under the user: show its top. MUI does that for its
      // own scroller on a page change, but not for the page's.
      scrollToListTop(scroller, listRef.current);
    }
    // Keep where the list is now: a scroller left above the list fires no
    // scroll event to save it.
    saved.resetScrollPosition(scrollKey, scroller?.scrollTop ?? 0);
  }, [scrollKey, getScroller, listRef]);

  useEffect(() => {
    if (scrollKey === undefined) return;
    const scroller = getScroller();
    if (!scroller) return;

    let frame: number | null = null;
    const handleScroll = () => {
      // Until the saved position is back, scroll events come from the list
      // loading or the router, and would overwrite it.
      if (pendingRestoreRef.current !== null || frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        useGridStore.getState().setScrollPosition(scroller.scrollTop);
      });
    };

    scroller.addEventListener('scroll', handleScroll);
    return () => {
      scroller.removeEventListener('scroll', handleScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [scrollKey, getScroller]);

  useEffect(() => {
    const target = pendingRestoreRef.current;
    if (scrollKey === undefined || !rowsReady || target === null) return;
    return restoreScrollTop(getScroller, target, () => {
      pendingRestoreRef.current = null;
    });
  }, [scrollKey, getScroller, rowsReady]);
}
